import React, { useState, useMemo } from 'react';
import { Activity, Filter, RefreshCw, ChevronDown, ChevronRight, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Users } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import {
  ResizablePanelGroup,
  ResizablePanel,
  ResizableHandle,
} from '@/components/ui/resizable';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { cn } from '@/lib/core/utils';
import { useShiftSchedulingActivityLog } from '@/hooks/scheduling/useShiftSchedulingActivityLog';
import { CompactShiftActivityLogRow } from './CompactShiftActivityLogRow';
import { ShiftSchedulingLogDetailPanel } from './ShiftSchedulingLogDetailPanel';
import type {
  ShiftSchedulingEventType,
  ShiftSchedulingActivityFilters,
} from '@/types/shift-scheduling-activity';
import { SCHEDULING_EVENT_CONFIG } from '@/types/shift-scheduling-activity';

interface ShiftSchedulingActivityLogCardProps {
  businessId: string | null;
  selectedDate: Date;
  employees?: Array<{ id: string; name: string }>;
  className?: string;
  onShiftClick?: (shiftId: string, shiftDate: Date) => void;
}

const EVENT_TYPE_OPTIONS: Array<{ value: ShiftSchedulingEventType; label: string; group?: string }> = [
  // Shift CRUD events
  { value: 'shift_created', label: 'Shift Created', group: 'Shift Operations' },
  { value: 'shift_edited', label: 'Shift Edited', group: 'Shift Operations' },
  { value: 'shift_moved', label: 'Shift Moved', group: 'Shift Operations' },
  { value: 'shift_deleted', label: 'Shift Deleted', group: 'Shift Operations' },
  // Assignment events
  { value: 'employee_assigned', label: 'Employee Assigned', group: 'Assignments' },
  { value: 'employee_unassigned', label: 'Employee Unassigned', group: 'Assignments' },
  // Self-service events
  { value: 'employee_self_requested', label: 'Shift Requested', group: 'Self-Service' },
  { value: 'employee_request_approved', label: 'Request Approved', group: 'Self-Service' },
  { value: 'employee_request_rejected', label: 'Request Rejected', group: 'Self-Service' },
];

export const ShiftSchedulingActivityLogCard: React.FC<ShiftSchedulingActivityLogCardProps> = ({
  businessId,
  selectedDate,
  employees = [],
  className,
  onShiftClick,
}) => {
  const [filters, setFilters] = useState<ShiftSchedulingActivityFilters>({});
  const [filterOpen, setFilterOpen] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);

  // Calculate date range (selected date + 6 days before for context)
  const dateRange = useMemo(() => {
    const end = new Date(selectedDate);
    const start = new Date(selectedDate);
    start.setDate(start.getDate() - 6);
    return { start, end };
  }, [selectedDate]);

  const { events, isLoading, error, refetch, groupedEvents } = useShiftSchedulingActivityLog({
    businessId,
    filters: { ...filters, dateRange },
    limit: 100,
  });

  // Get the selected event
  const selectedEvent = useMemo(() => {
    if (!selectedEventId) return null;
    return events.find((e) => e.id === selectedEventId) || null;
  }, [selectedEventId, events]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.employeeIds?.length) count += filters.employeeIds.length;
    if (filters.eventTypes?.length) count += filters.eventTypes.length;
    return count;
  }, [filters]);

  const handleEmployeeToggle = (employeeId: string) => {
    setFilters((prev) => {
      const current = prev.employeeIds || [];
      const updated = current.includes(employeeId)
        ? current.filter((id) => id !== employeeId)
        : [...current, employeeId];
      return { ...prev, employeeIds: updated.length ? updated : undefined };
    });
  };

  const handleEventTypeToggle = (eventType: ShiftSchedulingEventType) => {
    setFilters((prev) => {
      const current = prev.eventTypes || [];
      const updated = current.includes(eventType)
        ? current.filter((t) => t !== eventType)
        : [...current, eventType];
      return { ...prev, eventTypes: updated.length ? updated : undefined };
    });
  };

  const clearFilters = () => {
    setFilters({});
  };

  return (
    <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
      <Card className={cn('flex flex-col', isExpanded ? 'h-[650px]' : 'h-auto', className)}>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
          <CollapsibleTrigger asChild>
            <button className="flex items-center gap-2 hover:opacity-80 transition-opacity">
              {isExpanded ? (
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              )}
              <CardTitle className="text-sm font-medium">Scheduling Activity</CardTitle>
              {events.length > 0 && (
                <Badge className="text-xs">
                  {events.length} events
                </Badge>
              )}
            </button>
          </CollapsibleTrigger>

          <div className="flex items-center gap-2">
            {/* Filter Button */}
            <Popover open={filterOpen} onOpenChange={setFilterOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className={cn(
                  'h-8 gap-1',
                  activeFilterCount > 0 && 'bg-primary/10'
                )}
              >
                <Filter className="h-4 w-4" />
                Filter
                {activeFilterCount > 0 && (
                  <Badge className="ml-1 h-5 px-1 text-xs">
                    {activeFilterCount}
                  </Badge>
                )}
                <ChevronDown className="h-3 w-3 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <div className="flex flex-col">
                {/* Two-column layout for filters */}
                <div className="grid grid-cols-2 gap-0">
                  {/* Event Types Column */}
                  <div className="p-3 border-r">
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium text-sm flex items-center gap-2">
                          <Filter className="h-4 w-4" />
                          Event Types
                          {(filters.eventTypes?.length ?? 0) > 0 && (
                            <span className="bg-indigo-100 text-indigo-800 text-xs px-1.5 py-0.5 rounded-full">
                              {filters.eventTypes?.length}
                            </span>
                          )}
                        </h4>
                      </div>
                      <div className="flex justify-center" style={{gap: '0.25rem'}}>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 px-2 text-xs flex-1"
                          onClick={() => setFilters(prev => ({
                            ...prev,
                            eventTypes: EVENT_TYPE_OPTIONS.map(o => o.value)
                          }))}
                        >
                          All
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 px-2 text-xs flex-1"
                          onClick={() => setFilters(prev => ({ ...prev, eventTypes: undefined }))}
                        >
                          None
                        </Button>
                      </div>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {EVENT_TYPE_OPTIONS.map((option) => (
                          <div key={option.value} className="flex items-center space-x-2">
                            <Checkbox
                              id={`event-${option.value}`}
                              checked={filters.eventTypes?.includes(option.value) ?? false}
                              onCheckedChange={() => handleEventTypeToggle(option.value)}
                            />
                            <label
                              htmlFor={`event-${option.value}`}
                              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex items-center gap-2 cursor-pointer flex-1"
                            >
                              <div
                                className={cn(
                                  'w-2 h-2 rounded-full',
                                  SCHEDULING_EVENT_CONFIG[option.value].dotColor
                                )}
                                aria-hidden="true"
                              />
                              <span className="truncate">{option.label}</span>
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Employees Column */}
                  <div className="p-3">
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium text-sm flex items-center gap-2">
                          <Users className="h-4 w-4" />
                          Employees
                          {(filters.employeeIds?.length ?? 0) > 0 && (
                            <span className="bg-indigo-100 text-indigo-800 text-xs px-1.5 py-0.5 rounded-full">
                              {filters.employeeIds?.length}
                            </span>
                          )}
                        </h4>
                      </div>
                      <div className="flex justify-center" style={{gap: '0.25rem'}}>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 px-2 text-xs flex-1"
                          onClick={() => setFilters(prev => ({
                            ...prev,
                            employeeIds: employees.map(e => e.id)
                          }))}
                        >
                          All
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 px-2 text-xs flex-1"
                          onClick={() => setFilters(prev => ({ ...prev, employeeIds: undefined }))}
                        >
                          None
                        </Button>
                      </div>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {employees.map((employee) => (
                          <div key={employee.id} className="flex items-center space-x-2">
                            <Checkbox
                              id={`employee-${employee.id}`}
                              checked={filters.employeeIds?.includes(employee.id) ?? false}
                              onCheckedChange={() => handleEmployeeToggle(employee.id)}
                            />
                            <label
                              htmlFor={`employee-${employee.id}`}
                              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex items-center gap-2 cursor-pointer flex-1"
                            >
                              <div className="w-2 h-2 bg-green-500 rounded-full" aria-hidden="true" />
                              <span className="truncate">{employee.name}</span>
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Clear filters at bottom */}
                {activeFilterCount > 0 && (
                  <div className="border-t px-3 py-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full text-xs"
                      onClick={clearFilters}
                    >
                      <X className="h-3 w-3 mr-1" />
                      Clear all filters
                    </Button>
                  </div>
                )}
              </div>
            </PopoverContent>
          </Popover>

          {/* Refresh Button */}
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
            onClick={() => refetch()}
            disabled={isLoading}
          >
            <RefreshCw className={cn('h-4 w-4', isLoading && 'animate-spin')} />
          </Button>

          <Activity className="h-5 w-5 text-muted-foreground" />
        </div>
      </CardHeader>

        <CollapsibleContent className="flex-1 overflow-hidden">
          <CardContent className="h-full p-0">
            {error ? (
              <div className="flex items-center justify-center h-full text-destructive text-sm">
                Error loading activity log
              </div>
            ) : isLoading ? (
              <div className="flex items-center justify-center h-full text-muted-foreground">
                <div className="flex items-center gap-2">
                  <div className="h-4 w-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                  <span className="text-sm">Loading...</span>
                </div>
              </div>
            ) : events.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                <Activity className="h-10 w-10 mb-2 opacity-20" />
                <p className="text-sm">No scheduling activity recorded</p>
                <p className="text-xs">Shift changes will appear here in real-time</p>
              </div>
            ) : (
              <ResizablePanelGroup direction="horizontal" className="h-full">
                {/* Event List Panel */}
                <ResizablePanel defaultSize={selectedEvent ? 55 : 100} minSize={35}>
                  <ScrollArea className="h-full">
                    <div className="py-1">
                      {Array.from(groupedEvents.entries()).map(([dateGroup, groupEvents]) => (
                        <div key={dateGroup}>
                          {/* Date group header */}
                          <div className="sticky top-0 bg-background/95 backdrop-blur-sm z-10 px-2 py-1">
                            <h3 className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
                              {dateGroup}
                            </h3>
                          </div>

                          {/* Events in this group */}
                          <div>
                            {groupEvents.map((event) => (
                              <CompactShiftActivityLogRow
                                key={event.id}
                                event={event}
                                isSelected={selectedEventId === event.id}
                                onClick={() => setSelectedEventId(
                                  selectedEventId === event.id ? null : event.id
                                )}
                              />
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </ResizablePanel>

                {/* Detail Panel - only shown when event selected */}
                {selectedEvent && (
                  <>
                    <ResizableHandle withHandle />
                    <ResizablePanel defaultSize={45} minSize={30}>
                      <ShiftSchedulingLogDetailPanel
                        event={selectedEvent}
                        onClose={() => setSelectedEventId(null)}
                        onShiftClick={onShiftClick}
                      />
                    </ResizablePanel>
                  </>
                )}
              </ResizablePanelGroup>
            )}
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
};

export default ShiftSchedulingActivityLogCard;
